import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import { DialogConfirmComponent } from 'app/components/modal/dialog-confirm/dialog-confirm.component';
import { DialogGeneralMessageComponent } from 'app/components/modal/dialog-general-message/dialog-general-message.component';
import { TranslatePipe } from 'app/pipes/translate.pipe';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { UserService } from 'app/services/user/user.service';
import { Observable } from 'rxjs/Rx';

import { AddressFormComponent } from '../address-form/address-form.component';
import { DialogTaxAddressComponent } from './../modal/dialog-tax-address/dialog-tax-address.component';
import { DialogTaxAddressActionType, DialogTaxAddressData } from './../modal/dialog-tax-address/dialog-tax-address.model';
import { DialogConfirmType } from '../modal/dialog-confirm/dialog-confirm.model';
import { DialogSuccessComponent } from '../modal/dialog-success/dialog-success.component';
import { DialogErrorComponent } from '../modal/dialog-error/dialog-error.component';

@Component({
  selector: 'act-address-list',
  templateUrl: './address-list.component.html',
  styleUrls: ['./address-list.component.scss']
})
export class AddressListComponent implements OnInit, OnChanges {
  @Input() addressData;
  @Input() options = { page: 'profile', selectable: false, editable: true, editPopup: false };
  @Input() isCallbackEditFromParent = false;
  @Output() onFetchData = new EventEmitter();
  @Output() onSelected = new EventEmitter();
  @Output() onUpdated = new EventEmitter();
  @Output() onShowEditAddressList = new EventEmitter();
  @Output() onCloseEditAddressList = new EventEmitter();
  @Output() onCallEditFromParent = new EventEmitter();

  @ViewChild(AddressFormComponent) addressFormComponent: AddressFormComponent;
  @ViewChild('addressFormList') addressFormList: AddressFormComponent;
  @ViewChild('addressFormAdd') addressFormAdd: AddressFormComponent;
  @ViewChild('addressFormOneTime') addressFormOneTime: AddressFormComponent;


  private readonly limitAddress = 5;
  private isLoading: Boolean = false; // tslint:disable-line
  private addressDataStatus: object;
  private selectedAddress: any = { taxAddressId: -1 };
  private selectedEditAddress: any = { taxAddressId: -1 };
  private selectedDeleteAddress;
  private currentState;
  private formAddress = {};
  private oneTimeAddress: Boolean = false;

  private readonly STATE = {
    VIEW: 'VIEW',
    EDIT: 'EDIT',
    ADD: 'ADD'
  };

  private STATUS = {
    INITIAL: 'INITIAL',
    PENDING: 'PENDING',
    RESPONSE: 'RESPONSE',
  };

  private dataStatus = {
    isInitial: false,
    isPending: false,
    isResponse: false,
  };

  constructor(private dialog: MdDialog,
    private appConfigService: AppConfigService,
    private userService: UserService,
    private translatePipe: TranslatePipe) { }

  ngOnInit() {

  }

  ngOnChanges() {
    if (typeof this.addressData !== 'undefined' && this.addressData !== null) {
      this.addressData = this.addressData.map((address, idx) => {
        return { ...address, state: 'view', idx };
      });

      // Set default address
      // this.setSelectedAddress();
      this.setDataStatus(this.STATUS.RESPONSE);
    }
  }

  getDefaultSelectedAddress() {
    const [defaultAddress] = this.addressData.filter(address => address.isDefault);
    const address = defaultAddress ? defaultAddress : this.addressData ? this.addressData[0] : {};
    return { ...address };
  }

  setSelectedAddress(address = undefined) {
    this.selectedAddress = address || (this.isCreditCardBilling() ? this.getDefaultSelectedAddress() : { taxAddressId: -1 });
    this.onSelected.emit(this.selectedAddress);
  }

  onAddressSelected(address) {
    this.setSelectedAddress(address);
  }

  setDataStatus(status: string) {
    const { INITIAL, PENDING, RESPONSE } = this.STATUS;
    switch (status) {
      case INITIAL:
        this.addressDataStatus = { ...this.dataStatus, isInitial: true };
        break;
      case PENDING:
        this.addressDataStatus = { ...this.dataStatus, isPending: true };
        break;
      case RESPONSE:
        this.addressDataStatus = { ...this.dataStatus, isResponse: true };
        break;
      default:
        break;
    }
  }

  isEdit(address) {
    return (
      address.taxAddressId === this.selectedEditAddress.taxAddressId
      && this.selectedEditAddress.state === 'edit'
    );
  }

  isFormDirty() {
    return Object.keys(this.formAddress).length;
  }

  isCreditCardBilling() {
    return this.addressData && this.addressData.length > 0 && this.addressData[0].taxAddressId !== null;
  }

  isSelectedAddress(address, i?: number) {
    const isCreditCardBilling = this.isCreditCardBilling();
    const actualAddressId = isCreditCardBilling ? this.selectedAddress.taxAddressId : this.selectedAddress.companyBillingId;
    const expectedAddressId = isCreditCardBilling ? address.taxAddressId : address.companyBillingId;
    const matchAddress = actualAddressId === expectedAddressId;
    return (matchAddress && this.currentState !== this.STATE.ADD && this.currentState !== this.STATE.EDIT);
  }

  isChecked(address, i?: number) {
    return this.isCreditCardBilling() ? (
      this.isSelectedAddress(address) ||
      (this.currentState === this.STATE.VIEW && this.selectedAddress.taxAddressId === -1 && this.getDefaultSelectedAddress().idx === i)
    ) : this.isSelectedAddress(address);
  }

  canAddAddress() {
    return !!this.addressData && this.addressData.length < this.limitAddress;
  }

  clearFormDirty() {
    this.formAddress = {};
  }

  clearSelectedAddress() {
    this.selectedAddress = { taxAddressId: -1 };
  }

  clearSelectedEditAddress() {
    this.selectedEditAddress = {};
  }

  translate(value) {
    return this.translatePipe.transform(value);
  }

  createMessage({ type }) {
    const content = {
      message: {
        icon: 'icon-service-failed',
        title: this.translate('my-profile-edit-failure-title'),
        message: this.translate('my-profile-edit-failure-description'),
        mainBtn: this.translate('btn-ok')
      },
      remove: {
        confirmType: DialogConfirmType.CONFIRM_DELETE_ADDRESS,
        data: {
          confirmAction: this.handleDeleteAddressDialog.bind(this)
        }
      }
    };

    return content[type];
  }

  updateAddresses(address) {
    this.addressData = this.addressData.map((record) => {
      if (record.taxAddressId === address.taxAddressId) {
        return { ...record, ...address };
      }
      return record;
    });
  }

  handleDeleteAddress(address) {
    this.selectedDeleteAddress = { ...address };
    this.showDialog({ type: 'remove' });
  }

  handleSelectAddress(address) {
    if (this.isFormDirty()) {
      // not have event
    } else {
      this.currentState = this.STATE.VIEW;
      this.selectedAddress = { ...address, state: 'view' };
      this.selectedEditAddress = { ...address, state: 'view' };
    }
  }

  handleAddAddress() {
    if (this.options.editPopup) {
      this.dialog.open(DialogTaxAddressComponent)
        .afterClosed()
        .subscribe(this.handleAddressEditPopup.bind(this));
    } else {
      if (this.isCallbackEditFromParent) {
        this.onCallEditFromParent.emit({
          callback: () => {
            if (this.canAddAddress()) {
              this.currentState = this.STATE.ADD;
              this.clearSelectedAddress();
              this.clearSelectedEditAddress();
              this.onShowEditAddressList.emit();
            }
          }
        });
      } else {
        if (this.canAddAddress()) {
          this.currentState = this.STATE.ADD;
          this.clearSelectedAddress();
          this.clearSelectedEditAddress();
          this.onShowEditAddressList.emit();
        }
      }
    }
  }

  setViewModeOnFormValid() {
    if (this.addressFormComponent && this.addressFormComponent.form.valid && !!this.selectedAddress && this.addressData.length > 0) {
      const address = this.addressFormComponent.addressInfo;
      this.handleUpdateAddressInfo(address);
    }
  }

  exitEditMode() {
    this.currentState = this.STATE.VIEW;
  }

  showDialog({ type }) {
    const config = {
      hasBackdrop: true,
      disableClose: false,
      focusOnOpen: false,
      data: this.createMessage({ type })
    };

    const dialog = {
      remove: DialogConfirmComponent,
      message: DialogGeneralMessageComponent
    };

    const dialogRef = this.dialog.open(dialog[type], config);
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.onFetchData.emit();
      }
    });
  }

  handleEditAddress(address) {
    if (this.isCallbackEditFromParent) {
      this.onCallEditFromParent.emit({
        callback: () => {
          this.selectedEditAddress = { ...address, state: 'edit' };
          this.clearFormDirty();
          this.currentState = this.STATE.EDIT;
          this.onShowEditAddressList.emit();
        }
      });
    }
    else {
      if (this.options.editPopup) {
        this.dialog.open(DialogTaxAddressComponent, { data: address })
          .afterClosed()
          .subscribe(this.handleAddressEditPopup.bind(this));
      } else {
        if (this.isFormDirty()) {
          const config = { data: { confirmType: DialogConfirmType.CONFIRM_CLOSE } };
          const dialogRef: MdDialogRef<DialogConfirmComponent> = this.dialog.open(DialogConfirmComponent, config);
          dialogRef.afterClosed().subscribe(res => {
            if (res) {
              this.selectedEditAddress = { ...address, state: 'edit' };
              this.clearFormDirty();
              this.currentState = this.STATE.EDIT;
              this.onShowEditAddressList.emit();
            }
          });
        } else {
          this.selectedEditAddress = { ...address, state: 'edit' };
          this.clearFormDirty();
          this.currentState = this.STATE.EDIT;
          this.onShowEditAddressList.emit();
        }

        if (this.selectedAddress.taxAddressId === -1) {
          this.setSelectedAddress();
        }
      }
    }

  }

  // Collect form dirty
  handleEditAddressInfo(data) {
    this.formAddress = data;
  }

  handleCancelAddAddressInfo() {
    this.currentState = this.STATE.VIEW;
    this.clearFormDirty();
    this.setSelectedAddress();
    this.onCloseEditAddressList.emit();
  }

  handleAddAddressInfo(address) {
    this.currentState = this.STATE.VIEW;
    this.addressData = [...this.addressData, ...address];
    this.clearFormDirty();
    this.clearSelectedAddress();
    this.onFetchData.emit();
  }

  handleUpdateAddressInfo(address) {
    this.currentState = this.STATE.VIEW;
    this.selectedEditAddress = { ...address, state: 'view' };
    this.updateAddresses(address);
    this.clearFormDirty();
    this.onCloseEditAddressList.emit();
    this.onUpdated.emit(this.addressData);
  }

  handleClickSubmit() {
    if (this.addressFormComponent.onSubmit(this.addressFormComponent.form)) {
      this.setSelectedAddress();
    }
  }

  setSubmitting$() {
    if (!this.addressFormComponent) {
      return Observable.of(false);
    }

    return this.addressFormComponent.onSubmit$(this.addressFormComponent.form)
      .do((isValidAddress) => {
        !!isValidAddress && this.setSelectedAddress();
      });
  }

  isDefaultAddressChanged() {
    const defaultAddress = this.getDefaultSelectedAddress();
    const defaultId = defaultAddress.taxAddressId ? defaultAddress.taxAddressId : defaultAddress.companyBillingId;
    const expectedId = this.selectedAddress.taxAddressId ? this.selectedAddress.taxAddressId : this.selectedAddress.companyBillingId;
    return defaultId !== expectedId;
  }

  saveDefaultAddress$() {
    return this.userService.editTaxAddress({
      ...this.selectedAddress,
      isDefault: true,
      active: true,
    });
  }

  setOneTimeAddress(enable: Boolean, getTaxAddressByAPI: Boolean = false) {
    this.oneTimeAddress = enable && !getTaxAddressByAPI;
    this.currentState = this.oneTimeAddress ? this.STATE.ADD : this.STATE.VIEW;
  }

  getSelectedAddress() {
    return this.selectedAddress;
  }

  handleAddressEditPopup(res: DialogTaxAddressData) {
    if (res) {
      switch (res.action) {
        case DialogTaxAddressActionType.UPDATE_ADDRESS:
          this.handleAddAddressInfo(res.data);
          break;
      }
    }
  }

  handleDeleteAddressDialog() {
    return new Observable(subscriber => {
      const payload = this.selectedDeleteAddress;
      payload.active = false;
      const { Langid: langId, username: email } = this.userService.getUser();
      this.userService.editTaxAddress({ ...payload, langId, email })
        .subscribe(res => {
          subscriber.next(true);
          subscriber.complete();
          const dialogRef: MdDialogRef<DialogSuccessComponent> = this.dialog.open(DialogSuccessComponent);
          dialogRef.componentInstance.text = this.appConfigService.getTranslate('my-profile-delete-address-success');
        },
          error => {
            subscriber.next(true);
            subscriber.complete();
            const dialogRef: MdDialogRef<DialogErrorComponent> = this.dialog.open(DialogErrorComponent);
            dialogRef.componentInstance.text = this.appConfigService.getTranslate('my-profile-delete-address-failure');
            dialogRef.componentInstance.subText = this.appConfigService.getTranslate('my-profile-delete-address-failure-text');
          }
        );
    })
  }
}



// WEBPACK FOOTER //
// ./src/app/components/address-list/address-list.component.ts